import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { SalesOrderFormData } from '../../types/salesOrder';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';
import { useLanguage } from '../../features/language/useLanguage';

interface Props {
  control: Control<SalesOrderFormData>;
  register: UseFormRegister<SalesOrderFormData>;
  watch: UseFormWatch<SalesOrderFormData>;
  setValue: UseFormSetValue<SalesOrderFormData>;
  errors: FieldErrors<SalesOrderFormData>;
  areFieldsDisabled: boolean;
  isStatusDisabled: boolean;
  isPaymentStatusDisabled: boolean;
  restrictStatusOptions: boolean;
}

export const SalesOrderDetailsSection: React.FC<Props> = ({
  control,
  register,
  watch,
  setValue,
  errors,
  areFieldsDisabled,
  isStatusDisabled,
  isPaymentStatusDisabled,
  restrictStatusOptions
}) => {
  const { t } = useLanguage();
  const orderDate = watch('orderDate') || '';
  const billingAddress = watch('billingAddress') || '';
  const shippingAddress = watch('shippingAddress') || '';
  const prevBillingAddressRef = React.useRef(billingAddress);
  const prevShippingAddressRef = React.useRef(shippingAddress);

  const derivedSameAddress =
    billingAddress.trim().length > 0 && shippingAddress.trim() === billingAddress.trim();

  const [isShippingSameAsBilling, setIsShippingSameAsBilling] = React.useState(derivedSameAddress);

  React.useLayoutEffect(() => {
    setIsShippingSameAsBilling(derivedSameAddress);
  }, [derivedSameAddress]);

  React.useEffect(() => {
    if (areFieldsDisabled) return;
    if (!isShippingSameAsBilling) return;

    const prevBillingAddress = prevBillingAddressRef.current;
    const prevShippingAddress = prevShippingAddressRef.current;
    const billingChanged = billingAddress !== prevBillingAddress;
    const shippingChanged = shippingAddress !== prevShippingAddress;
    const previouslySameAddress =
      prevBillingAddress.trim().length > 0 && prevShippingAddress.trim() === prevBillingAddress.trim();

    if (!billingChanged) return;
    if (shippingChanged) return;
    if (!previouslySameAddress) return;
    if (shippingAddress === billingAddress) return;
    setValue('shippingAddress', billingAddress, { shouldValidate: true, shouldDirty: true });
  }, [areFieldsDisabled, billingAddress, isShippingSameAsBilling, setValue, shippingAddress]);

  React.useEffect(() => {
    prevBillingAddressRef.current = billingAddress;
    prevShippingAddressRef.current = shippingAddress;
  }, [billingAddress, shippingAddress]);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formDetails.billingAddress')}</label>
            <textarea
              data-test-id="so-billing-address"
              {...register('billingAddress')}
              disabled={areFieldsDisabled}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            />
          </div>
          <div>
            <div className="flex items-center justify-between gap-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('salesOrder.formDetails.shippingTo')} <span className="text-red-500">*</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={isShippingSameAsBilling}
                  disabled={areFieldsDisabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsShippingSameAsBilling(checked);
                    if (checked) {
                      setValue('shippingAddress', billingAddress, { shouldValidate: true, shouldDirty: true });
                    } else if (shippingAddress.trim() === billingAddress.trim()) {
                      setValue('shippingAddress', '', { shouldValidate: true, shouldDirty: true });
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {t('salesOrder.formDetails.sameAsBilling')}
              </label>
            </div>
            <textarea
              data-test-id="so-shipping-address"
              {...register('shippingAddress')}
              disabled={areFieldsDisabled || isShippingSameAsBilling}
              rows={3}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-primary-500 sm:text-sm border p-2 ${
                errors.shippingAddress ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-primary-500'
              }`}
            />
            {errors.shippingAddress && (
              <p className="mt-1 text-sm text-red-600">{errors.shippingAddress.message}</p>
            )}
          </div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formDetails.expectedShipDate')}</label>
              <input
                disabled={areFieldsDisabled}
                data-test-id="so-create-expected-date"
                type="date"
                {...register('expectedShipDate')}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.list.table.deliveryStatus')}</label>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <select
                    data-test-id="so-create-status"
                    {...field}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                    disabled={isStatusDisabled}
                  >
                    {restrictStatusOptions ? (
                      <>
                        <option value="Delivered">{t('salesOrder.list.status.delivered')}</option>
                        <option value="Cancelled">{t('salesOrder.list.status.cancelled')}</option>
                      </>
                    ) : (
                      <>
                        <option value="Draft">{t('salesOrder.list.status.draft')}</option>
                        <option value="Sent">{t('salesOrder.list.status.sent')}</option>
                        <option value="Delivered">{t('salesOrder.list.status.delivered')}</option>
                        <option value="Cancelled">{t('salesOrder.list.status.cancelled')}</option>
                      </>
                    )}
                  </select>
                )}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.payment.statusLabel')}</label>
              <Controller
                control={control}
                name="paymentStatus"
                render={({ field }) => (
                  <select
                    data-test-id="so-create-payment-status"
                    {...field}
                    value={field.value ?? 'unpaid'}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                    disabled={isPaymentStatusDisabled}
                  >
                    <option value="unpaid">{t('salesOrder.payment.status.unpaid')}</option>
                    <option value="paid">{t('salesOrder.payment.status.paid')}</option>
                    <option value="refunded">{t('salesOrder.payment.status.refunded')}</option>
                  </select>
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formDetails.shipVia')}</label>
              <input
                disabled={areFieldsDisabled}
                data-test-id="so-ship-via"
                type="text"
                {...register('shipVia')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formDetails.shippingDate')}</label>
              <input
                disabled={areFieldsDisabled}
                data-test-id="so-shipping-date"
                type="date"
                {...register('shippingDate')}
                min={orderDate}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
              {errors.shippingDate && <p className="mt-1 text-sm text-red-600">{errors.shippingDate.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formDetails.trackingNo')}</label>
              <input
                disabled={areFieldsDisabled}
                data-test-id="so-tracking-number"
                type="text"
                {...register('trackingNumber')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formDetails.sales')}</label>
              <input
                disabled={areFieldsDisabled}
                data-test-id="so-business-development"
                type="text"
                {...register('businessDevelopmentExecutive')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formDetails.tags')}</label>
              <input
                disabled={areFieldsDisabled}
                data-test-id="so-tags"
                type="text"
                {...register('tags')}
                placeholder={t('salesOrder.formDetails.tagsPlaceholder')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
